import { debounce } from "lodash";
import { CreateProps } from "ra-core/esm/controller/details/useCreateController";
import { createContext, Dispatch, FC, MutableRefObject, SyntheticEvent, useContext, useEffect, useMemo, 
  useRef, useState } from "react";
import { raiseRedirectExternal } from "~/actions/redirectExternal";
import { LoginResponseDto } from "~/entry/types";


export interface Phase1Input {
  username: string;
  password: string;
}

export interface Phase2Input {
  vCode: string;
}

export enum Status {
  DetermineSignIn = 0,
  Phase1 = 1,
  Phase2 = 2,
}

export interface States {
  username: string;
  password: string;
  phase1Result: LoginResponseDto | undefined;
  status: Status;
  determining: boolean;
  determined: boolean;
  ssoRedirectUrl?: {url:string};
  ssoUsername: string;
  explicitLocal: boolean;
  errMsg: string;
}

export type Action =
| { type: 'error_occur', error: string}
| { type: 'error_clear' }
| { type: 'p1_preload_error', error: string}
| { type: 'go_local' }
| { type: 'reset' }
| { type: 'p1_preload', email: string }
| { type: 'p1_sso', redirectUrl: {url:string} }
| { type: 'p1_setback' }
| { type: 'p1_done', data: {
    username: string;
    password: string;
    phase1Result: LoginResponseDto;
    status: Status.Phase2;} }

export const buildReducer = ({dispatchEffect}: { dispatchEffect: (x:any)=>any }) => (v: States, action: Action):States => {
  switch (action.type) {
    case 'error_occur': { return {...v, errMsg: translateError(action.error)} }
    case 'error_clear': { return v.errMsg ? {...v, errMsg: ''} : v }
    case 'p1_preload_error': { return {...v, errMsg: translateError(action.error), determining: false} }
    case 'go_local': { return {...v, explicitLocal: true, status: Status.Phase1 } }
    case 'p1_preload': { return {...v, 
      ssoUsername: action.email,
      determined: false,
      determining: true,
      errMsg: '' } }
    case 'p1_sso': { 
      const url = action.redirectUrl?.url
      if (url) { 
        setTimeout(() => dispatchEffect(raiseRedirectExternal(url))) 
      }
      return {...v, 
        ssoRedirectUrl: action.redirectUrl,
        determining: false,
        determined: true, 
        ...(!url && { status: Status.Phase1 })
      } 
    }
    case 'p1_setback': { return {...v, status: Status.Phase1 } }
    case 'p1_done': { 
      return {...v, ...action.data};
    }
    case 'reset': {
      return {
        username: '',
        password: '',
        phase1Result: undefined,
        status: Status.DetermineSignIn,
        determining: false,
        determined: false,
        ssoRedirectUrl: {url:''},
        ssoUsername: '',
        explicitLocal: false,
        errMsg: '',
      }
    }
    default: return v;
  }
}

export interface LoginControllerProps {
  deviceFlow?: { usercode: string };
  states: States;
  bag: any;
  dispatch: Dispatch<Action>;
  Create: FC<CreateProps>;
}

const LoginContext = createContext<LoginControllerProps>({
  states: {} as any,
  bag: {} as any,
  dispatch: () => {},
  Create: () => null,
})


export const LoginContextProvider: FC<{ value: LoginControllerProps }> = ({ children, value }) => {
  return (
    <LoginContext.Provider value={value}>
      {children}
    </LoginContext.Provider>
  )
}

export const useLoginContext = () => useContext(LoginContext);



export const SrRegion : FC<{boxRef:any}> = ({ children, boxRef, ...rest }) => {
  const { states } = useLoginContext()
  const { formStates: { errors, touched } } = useSimpleFormContext()
  const [srAlert, setSrAlert] = useState('')
  const promptSR = useMemo(() => {
    return debounce((errorsBag:Record<string, string>, touchedBag:Record<string, boolean> ) => {
      setSrAlert(
        Object.keys(errorsBag).flatMap(k => touchedBag[k] && errorsBag[k] ? [errorsBag[k]] : []).join(' ')
      )
    }, 500)
  }, [])
  useEffect(() => {
    promptSR({...errors, submit: states.errMsg} as any, touched as any)
  }, [states.errMsg, JSON.stringify(Object.values(errors)), JSON.stringify(touched)])

  return (
    <div ref={boxRef} {...rest}>
      <div>
        {children}
      </div>
      <div className="sr-only" role="alert" aria-live="polite">
        {srAlert}
      </div>
    </div>
  )
}

interface SimpleFormContextProps {
  formStates: {
    errors: Record<string, string>,
    touched: Record<string, boolean>,
  },
  parentNodeRef: MutableRefObject<Node | undefined>,
  fieldProps: (name: string, setter: (value: string) => void) => {
    name: string,
    error: boolean,
    helperText: string|false,
    onChange: (e: { target: { value: string } }) => void,
    onBlur: (e: { target: { value: string } }) => void,
  };
  reset: () => void;
  forceValidate: (values: Record<string, string>) => {
    errors: any;
    touched: any;
  },
}

const SimpleFormContext = createContext<SimpleFormContextProps>({
  formStates: { errors: {}, touched: {} },
  fieldProps: (()=>{}) as any,
  reset: ()=>{},
  forceValidate: (()=>{}) as any,
  parentNodeRef: null as any,
})

export const SimpleFormContextProvider : FC<{value: SimpleFormContextProps}> = ({
  children,
  value,
}) => {
  return (
    <SimpleFormContext.Provider value={value}>
      {children}
    </SimpleFormContext.Provider>
  )
}

export const useSimpleFormContext = () => useContext(SimpleFormContext)

export const useSimpleForm = (validators: Record<string, (x:string)=>string|undefined>) => {
  const [formStates, setFormStates] = useState({
    errors: {} as Record<string, string>,
    touched: {} as Record<string, boolean>,
  })
  const {current:timers} = useRef({} as Record<string, number|undefined>)
  const parentNodeRef = useRef<Node>()

  const fieldProps = useMemo(() => {
    const buildChangeBlurHandlers = (
      name: string, 
      setter: (value:string)=>void, 
      validator: (x: string) => string|undefined      
    ) => { 
      const touchValue = (value:string, shouldSet: boolean) => {
        if (shouldSet) { setter(value) }
        let error = undefined as string|undefined
        if (!!validator) {
          error = validator(value)
        }
        setFormStates(v => {
          if (error === v.errors[name] && !!v.touched[name]) { return v }
  
          return {
            touched: { ...v.touched, [name]: true },
            errors: !error 
              ? (delete v.errors[name], v.errors) 
              : { ...v.errors, [name]: error }
          }
        })
      }
      const onChange = (e: {target:{value:string}}) => {
        const value = e.target.value
        touchValue(value, true)
        const timer = timers[name]
        if (timer) { window.clearTimeout(timer) }
        timers[name] = window.setTimeout(() => {
          const trimed = value && typeof(value) === 'string' ? value.trim() : value
          touchValue(trimed, true)
          delete timers[name]
        }, 1500)
      }
      const onBlur = (e: SyntheticEvent) => {
        if (e.target && (e.target as any).value !== undefined) {
          const value = (e.target as any).value
          if (parentNodeRef.current != null && !parentNodeRef.current.contains((e.nativeEvent as any).relatedTarget as any)) {
            // if we can confirm the focus shift out of the parent node, don't trigger touch/validate
            return;
          }
          touchValue(value, false)
        }
      }
      return {onChange, onBlur}
    }

    return (name:string, setter:(value:string)=>void) => {
      const validator = validators[name]
      
      return {
        name,
        error: Boolean(formStates.errors[name]),
        helperText: formStates.touched[name] && formStates.errors[name],
        ...(buildChangeBlurHandlers(name, setter, validator) as any),
      }
    }
  }, [validators, formStates, timers])
  
  const reset = () => setFormStates({ errors:{}, touched:{} })

  const forceValidate = (values: Record<string, string>) => {
    const errors = {} as any, touched = {} as any
    for(const name of Object.keys(values)) {
      const validator = validators[name]
      if (!validator) { continue; }
      const value = values[name]
      touched[name] = true
      const error = validator(value)
      if (error) { errors[name] = error }
    }
    const newStates = { errors, touched }
    setFormStates(newStates)
    return newStates
  }

  return {
    formStates,
    fieldProps,
    reset,
    forceValidate,
    parentNodeRef: ((x: any) => { parentNodeRef.current = x }) as any,
  }
}

// Taken from HTML spec: https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
export const rEmail =
  // eslint-disable-next-line
  /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export const rPassword =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$^+=!*()@%&]).{12,}$/

const errorMap : Record<string, string> = {
  'invalid_client': "You don't seem to be working on a valid client. Your administrator may have suspended your app.",
  'Failed to fetch': 'The network connection is unstable.',
}

const translateError = (errorCode: string) => {
  return errorMap[errorCode] ?? errorCode
}
